import type { PluginConfiguration } from '../helpers'
import { icons } from '../icons'

const SAFE_AREA_STORAGE_KEY = 'fu-devtools-safe-area'
const SAFE_AREA_COLOR = 'rgba(0, 211, 243, 0.15)'

type SafeAreaSettings = {
  enabled: boolean
  top: number
  bottom: number
  left: number
  right: number
}

type PresetValues = Omit<SafeAreaSettings, 'enabled'>

const defaultSettings: SafeAreaSettings = {
  enabled: false,
  top: 32,
  bottom: 32,
  left: 32,
  right: 32,
}

const PRESETS: Record<string, PresetValues> = {
  'iphone-portrait': { top: 59, bottom: 34, left: 0, right: 0 },
  'iphone-landscape': { top: 0, bottom: 21, left: 55, right: 55 },
}

const loadSettings = (): SafeAreaSettings => {
  try {
    const stored = window.localStorage.getItem(SAFE_AREA_STORAGE_KEY)
    if (stored) {
      return { ...defaultSettings, ...JSON.parse(stored) }
    }
  } catch {
    /* ignore */
  }
  return { ...defaultSettings }
}

const saveSettings = (settings: SafeAreaSettings) => {
  try {
    window.localStorage.setItem(SAFE_AREA_STORAGE_KEY, JSON.stringify(settings))
  } catch {
    /* ignore */
  }
}

const OVERLAY_IDS = {
  top: 'fu-tools-safe-area-top',
  bottom: 'fu-tools-safe-area-bottom',
  left: 'fu-tools-safe-area-left',
  right: 'fu-tools-safe-area-right',
} as const

const createOverlay = (id: string, positionStyles: string) => {
  const existing = document.getElementById(id)
  if (existing) {
    existing.style.cssText = [
      'position: fixed',
      'z-index: 99998',
      'pointer-events: none',
      `background: ${SAFE_AREA_COLOR}`,
      positionStyles,
    ].join('; ')
    return
  }
  const el = document.createElement('div')
  el.id = id
  el.style.cssText = [
    'position: fixed',
    'z-index: 99998',
    'pointer-events: none',
    `background: ${SAFE_AREA_COLOR}`,
    positionStyles,
  ].join('; ')
  document.body.appendChild(el)
}

const showOverlays = (settings: SafeAreaSettings) => {
  if (settings.top > 0) {
    createOverlay(OVERLAY_IDS.top, `top: 0; left: 0; width: 100%; height: ${settings.top}px`)
  } else {
    document.getElementById(OVERLAY_IDS.top)?.remove()
  }
  if (settings.bottom > 0) {
    createOverlay(
      OVERLAY_IDS.bottom,
      `bottom: 0; left: 0; width: 100%; height: ${settings.bottom}px`
    )
  } else {
    document.getElementById(OVERLAY_IDS.bottom)?.remove()
  }
  if (settings.left > 0) {
    createOverlay(OVERLAY_IDS.left, `top: 0; left: 0; width: ${settings.left}px; height: 100vh`)
  } else {
    document.getElementById(OVERLAY_IDS.left)?.remove()
  }
  if (settings.right > 0) {
    createOverlay(OVERLAY_IDS.right, `top: 0; right: 0; width: ${settings.right}px; height: 100vh`)
  } else {
    document.getElementById(OVERLAY_IDS.right)?.remove()
  }
}

const hideOverlays = () => {
  for (const id of Object.values(OVERLAY_IDS)) {
    document.getElementById(id)?.remove()
  }
}

const applyVars = (settings: SafeAreaSettings) => {
  document.documentElement.style.setProperty('--spacing-safe-area-top', `${settings.top}px`)
  document.documentElement.style.setProperty('--spacing-safe-area-bottom', `${settings.bottom}px`)
  document.documentElement.style.setProperty('--spacing-safe-area-left', `${settings.left}px`)
  document.documentElement.style.setProperty('--spacing-safe-area-right', `${settings.right}px`)
}

const removeVars = () => {
  document.documentElement.style.removeProperty('--spacing-safe-area-top')
  document.documentElement.style.removeProperty('--spacing-safe-area-bottom')
  document.documentElement.style.removeProperty('--spacing-safe-area-left')
  document.documentElement.style.removeProperty('--spacing-safe-area-right')
}

const applyState = (settings: SafeAreaSettings) => {
  if (!settings.enabled) {
    hideOverlays()
    removeVars()
    return
  }
  showOverlays(settings)
  applyVars(settings)
}

const readInputValues = (): PresetValues => {
  const getValue = (side: string) => {
    const el = document.querySelector<HTMLInputElement>(`[data-fu-tools-safe-area-side="${side}"]`)
    return el ? Math.max(0, Number(el.value) || 0) : 0
  }
  return {
    top: getValue('top'),
    bottom: getValue('bottom'),
    left: getValue('left'),
    right: getValue('right'),
  }
}

const createNumberInput = (side: string, value: number) => {
  const el = document.createElement('input')
  el.type = 'number'
  el.min = '0'
  el.defaultValue = String(value)
  el.className =
    'w-14 bg-[#1e293b] text-white text-xs text-center rounded p-1 border-0 focus:ring-1 focus:ring-[#22d3ee] focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
  el.setAttribute('data-fu-tools-safe-area-side', side)
  return el
}

const config: PluginConfiguration = {
  name: 'Safe Area',
  id: 'safe-area',
  icon: icons.safeArea,
  extra: '',
  layout: {
    type: 'full',
    render: () => {
      const settings = loadSettings()

      const root = document.createElement('div')

      // Header
      const header = document.createElement('div')
      header.className =
        'pt-4 px-4 flex items-center justify-between mb-2 text-white gap-2 font-semibold'
      header.textContent = 'Safe Area'
      root.appendChild(header)

      // Description
      const desc = document.createElement('div')
      desc.className = 'px-4 pb-3 text-sm text-[#6F91B7]'
      desc.textContent =
        'Overlay and CSS variable simulation of device safe area insets. Changes take effect immediately without a page reload.'
      root.appendChild(desc)

      // Presets
      const presetsSection = document.createElement('div')
      presetsSection.className = 'px-4 pb-3 flex flex-col gap-2'

      const presetsLabel = document.createElement('div')
      presetsLabel.className = 'text-xs text-[#94a3b8] uppercase font-semibold'
      presetsLabel.textContent = 'Presets'
      presetsSection.appendChild(presetsLabel)

      const presetsRow = document.createElement('div')
      presetsRow.className = 'flex gap-2'

      const presetButtonClass =
        'flex-1 border-0 text-white text-xs transition-all bg-[#334155] hover:bg-[#1e293b] rounded-md cursor-pointer p-2 text-center'

      const portraitBtn = document.createElement('button')
      portraitBtn.type = 'button'
      portraitBtn.className = presetButtonClass
      portraitBtn.textContent = 'iPhone Portrait ↑'
      portraitBtn.setAttribute('data-fu-tools-safe-area-preset', 'iphone-portrait')
      presetsRow.appendChild(portraitBtn)

      const landscapeBtn = document.createElement('button')
      landscapeBtn.type = 'button'
      landscapeBtn.className = presetButtonClass
      landscapeBtn.textContent = 'iPhone Landscape →'
      landscapeBtn.setAttribute('data-fu-tools-safe-area-preset', 'iphone-landscape')
      presetsRow.appendChild(landscapeBtn)

      presetsSection.appendChild(presetsRow)
      root.appendChild(presetsSection)

      // Inset inputs — cross layout
      const insetSection = document.createElement('div')
      insetSection.className = 'px-4 pb-4 flex flex-col gap-1'

      const insetLabel = document.createElement('div')
      insetLabel.className = 'text-xs text-[#94a3b8] uppercase font-semibold mb-1'
      insetLabel.textContent = 'Insets'
      insetSection.appendChild(insetLabel)

      const pxLabel = (text: string) => {
        const el = document.createElement('span')
        el.className = 'text-xs text-[#6F91B7]'
        el.textContent = text
        return el
      }

      const inputWithPx = (side: string, value: number) => {
        const wrap = document.createElement('div')
        wrap.className = 'flex items-center gap-1'
        wrap.appendChild(createNumberInput(side, value))
        wrap.appendChild(pxLabel('px'))
        return wrap
      }

      // Top row
      const topRow = document.createElement('div')
      topRow.className = 'flex justify-center'
      const topWrap = document.createElement('div')
      topWrap.className = 'flex flex-col items-center gap-0.5'
      topWrap.appendChild(pxLabel('Top'))
      topWrap.appendChild(inputWithPx('top', settings.top))
      topRow.appendChild(topWrap)
      insetSection.appendChild(topRow)

      // Middle row: left + right
      const midRow = document.createElement('div')
      midRow.className = 'flex justify-between items-center'

      const leftWrap = document.createElement('div')
      leftWrap.className = 'flex flex-col items-center gap-0.5'
      leftWrap.appendChild(pxLabel('Left'))
      leftWrap.appendChild(inputWithPx('left', settings.left))

      const rightWrap = document.createElement('div')
      rightWrap.className = 'flex flex-col items-center gap-0.5'
      rightWrap.appendChild(pxLabel('Right'))
      rightWrap.appendChild(inputWithPx('right', settings.right))

      midRow.appendChild(leftWrap)
      midRow.appendChild(rightWrap)
      insetSection.appendChild(midRow)

      // Bottom row
      const bottomRow = document.createElement('div')
      bottomRow.className = 'flex justify-center'
      const bottomWrap = document.createElement('div')
      bottomWrap.className = 'flex flex-col items-center gap-0.5'
      bottomWrap.appendChild(pxLabel('Bottom'))
      bottomWrap.appendChild(inputWithPx('bottom', settings.bottom))
      bottomRow.appendChild(bottomWrap)
      insetSection.appendChild(bottomRow)

      root.appendChild(insetSection)

      // Toggle
      const toggleSection = document.createElement('div')
      toggleSection.className = 'flex flex-col gap-4 px-4 pb-4'

      const label = document.createElement('label')
      label.className = 'inline-flex gap-2 items-center cursor-pointer text-white'

      const checkbox = document.createElement('input')
      checkbox.type = 'checkbox'
      checkbox.className = 'sr-only peer'
      checkbox.setAttribute('data-fu-tools-safe-area-toggle', '')
      if (settings.enabled) {
        checkbox.defaultChecked = true
      }

      const slider = document.createElement('div')
      slider.className =
        "relative w-7 h-4 bg-[#334155] rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-[#cbd5e1] after:border after:rounded-full after:w-3 after:h-3 after:transition-all peer-checked:bg-[#22d3ee]"

      const labelText = document.createElement('span')
      labelText.textContent = 'Show safe area'

      label.appendChild(checkbox)
      label.appendChild(slider)
      label.appendChild(labelText)
      toggleSection.appendChild(label)
      root.appendChild(toggleSection)

      return root.outerHTML
    },
  },
  init: () => {
    const currentSettings = loadSettings()
    applyState(currentSettings)

    const toggle = document.querySelector<HTMLInputElement>('[data-fu-tools-safe-area-toggle]')

    const getAndSave = (): SafeAreaSettings => {
      const enabled = toggle?.checked ?? false
      const settings: SafeAreaSettings = { enabled, ...readInputValues() }
      saveSettings(settings)
      return settings
    }

    toggle?.addEventListener('change', () => {
      applyState(getAndSave())
    })

    for (const side of ['top', 'bottom', 'left', 'right']) {
      const input = document.querySelector<HTMLInputElement>(
        `[data-fu-tools-safe-area-side="${side}"]`
      )
      input?.addEventListener('input', () => {
        const settings = getAndSave()
        if (settings.enabled) {
          applyState(settings)
        }
      })
    }

    const presetButtons = document.querySelectorAll<HTMLButtonElement>(
      '[data-fu-tools-safe-area-preset]'
    )
    presetButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.getAttribute('data-fu-tools-safe-area-preset') ?? ''
        const preset = PRESETS[key]
        if (!preset) {
          return
        }

        for (const side of ['top', 'bottom', 'left', 'right']) {
          const input = document.querySelector<HTMLInputElement>(
            `[data-fu-tools-safe-area-side="${side}"]`
          )
          if (input) {
            input.value = String(preset[side as keyof PresetValues])
          }
        }

        const enabled = toggle?.checked ?? false
        const settings: SafeAreaSettings = { enabled, ...preset }
        saveSettings(settings)
        if (enabled) {
          applyState(settings)
        }
      })
    })
  },
  render: () => {
    // no external events for this plugin
  },
}

export const safeArea = config
